import { Fragment, useRef, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { apiFetch } from "../api/client";
import { useNotification } from "./Notifications";
import { mapApiErrorToNotification } from "../utils/notificationHelpers";

export default function AddInventory({ product, onClose, onSaved }) {
  const [quantity, setQuantity] = useState("");
  const [dateTime, setDateTime] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const notify = useNotification();
  const cancelButtonRef = useRef(null);

  // populate date/time when modal mounts (so it's fixed and not editable)
  useEffect(() => {
    setDateTime(new Date().toLocaleString());
  }, []);

  const saveInventory = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      if (!quantity) {
        notify("error", { title: "Validation", text: "Please enter quantity." });
        setIsSubmitting(false);
        return;
      }

      const parseNumber = (v) => {
        if (v === null || v === undefined) return 0;
        const s = String(v).replace(/[^0-9.-]+/g, "");
        const n = Number(s);
        return Number.isNaN(n) ? 0 : n;
      };

      const productId = product?.id || product?.productId || product?._id;
      if (!productId) {
        notify("error", { title: "Error", text: "Product id not found." });
        setIsSubmitting(false);
        return;
      }

      const payload = {
        ProductId: productId,
        Quantity: parseInt(quantity, 10) || 0,
        // CostValue - default to product purchase cost when available
        CostValue: parseNumber(product.purchaseCost ?? product.PurchaseCost ?? 0),
      };

      const res = await apiFetch(`/api/inventory`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const parsed = await res.json().catch(() => null);
        const note = mapApiErrorToNotification(parsed, res.status);
        notify("error", note);
        return;
      }
      notify("success", { title: "Inventory added", text: "Inventory added successfully." });
      if (onSaved && typeof onSaved === "function") onSaved();
      onClose();
    } catch (err) {
      console.error(err);
      notify("error", { title: "Network error", text: "Could not reach server." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white p-6 text-left shadow-xl transition-all w-full max-w-md">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-semibold leading-6 text-gray-900 mb-4"
                >
                  Add Inventory for {product.name}
                </Dialog.Title>

                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-900">
                    Quantity
                  </label>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="Enter quantity"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-900">
                    Date & Time
                  </label>
                  <input
                    type="text"
                    value={dateTime}
                    readOnly
                    className="bg-gray-100 border border-gray-300 text-gray-700 text-sm rounded-lg w-full p-2.5 cursor-not-allowed"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={saveInventory}
                    disabled={isSubmitting}
                    aria-busy={isSubmitting}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                  >
                    {isSubmitting && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                      </svg>
                    )}
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                  <button
                    onClick={onClose}
                    ref={cancelButtonRef}
                    className="px-4 py-2 bg-gray-200 text-gray-900 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
